import {reactive} from "vue";
import type {StandardAvailabilityData} from "@/frontendClasses";
import {get, findIndex} from 'lodash-es';
import type {ChosenReservationModel} from "@/serviceWorker/workerComponents/Models/ChosenReservationModel";
import type {GroupReservationModel} from "@/serviceWorker/workerComponents/Models/GroupReservationModel";
import type {OfferModel} from "@/serviceWorker/workerComponents/Models/OfferModel";
import {deserialize, serialize} from "@/utilities/serializer";
import {ReservationSearchData} from "@/serviceWorker/workerComponents/Models/ReservationSearchData";
import {NfHotelShopStore} from "@/store/NfHotelShopStore";


/**
 * Odpowiada za trzymanie informacji jakie standardy oraz rezerwacje są w koszyku.
 * stan zapisania koszyka z rezerwacjami do localstorage w przypadku gdy nie dokończylismy zakupu.
 * po zakończeniu procesu rezerwacji powinno czyścić koszyk metodą removeLocalBasket.
 */
export const NfBasketStore = reactive({
    basket: [] as {
        standard: StandardAvailabilityData,
        reservation: ChosenReservationModel,
        offer: OfferModel,
    }[],
    items: null as GroupReservationModel| null,
    widgetId: null as string | null,

    addToBasket(
        newItem: {
            standard: StandardAvailabilityData,
            reservation: ChosenReservationModel,
            offer: OfferModel,
        }
    ): void {
        this.basket.push(newItem);
    },
    removeFromBasket(reservation: ChosenReservationModel): void {
        const index = findIndex((get(this.items, 'reservations', [])), item =>
            get(item, 'reservationUniqueId') === reservation.reservationUniqueId);
        const basketIndex = findIndex(this.basket, item => {
            return item.reservation.reservationUniqueId === reservation.reservationUniqueId
        })
        if (index !== -1) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            this.items.reservations.splice(index, 1);
            if(!this.items?.reservations.length && this.widgetId) {
                localStorage.removeItem('nf-basket-' + this.widgetId);
            }
            else {
                this.saveLocalBasket(this.items as GroupReservationModel)
            }
        }

        if (basketIndex !== -1) {
            this.basket.splice(basketIndex, 1);
        }

    },
    availableCountStandard(_standard: StandardAvailabilityData): number {
        const standards = this.basket.filter(std =>
            std.standard!.availability!.id === _standard!.availability!.id);

        const onItems = this.items?.reservations
            .filter(res => {
            return get(_standard, 'availability.id', 0) === +res.chosenPrices.room_standard_id
            }) || [];


        if (!standards.length && !onItems.length) return get(_standard, 'availability.available', 0);
        else {
            const roomAvailable = get(_standard, 'availability.available', 0);
            return roomAvailable - (standards.length + onItems.length);
        }
    },

    saveLocalBasket(items: GroupReservationModel): void {
        if (this.widgetId) {
            const basketData = {
                items,
                timestamp: window.moment().toISOString(),
                shopStore: {
                    shops: NfHotelShopStore.shops,
                    reservations: NfHotelShopStore.reservations,
                    languageId: NfHotelShopStore.languageId,
                    widgetId: NfHotelShopStore.widgetId,
                }
            };
            localStorage.setItem('nf-basket-' + this.widgetId, JSON.stringify(serialize(basketData)));
        }
    },

    selectedCountStandard(_standard: StandardAvailabilityData, offer: OfferModel): number {
        const onBasket = this.basket.filter(std =>
            std.standard!.availability!.id === _standard!.availability!.id
            && get(offer, 'offerModel.id', get(offer, 'id')) === std.offer.id);

        const selected = this.items?.reservations
            .filter(res =>
                res.chosenOffer.id === get(offer, 'offerModel.id', '') && +get(_standard, 'availability.id', 0) === +res.chosenPrices.room_standard_id
            );

        return (selected ? selected.length : 0) + (onBasket ? onBasket.length : 0);
    },

    loadLocalBasket() {
        let output = null;
        if (this.widgetId) {
            const savedData = localStorage.getItem('nf-basket-' + this.widgetId);

            if (savedData) {
                const savedBasket = deserialize(JSON.parse(savedData));
                const { items, timestamp } = savedBasket;
                const weekAgo = window.moment().subtract(7, 'days');

                if (window.moment(timestamp).isBefore(weekAgo)) {
                    output = null;
                    localStorage.removeItem('nf-basket-' + this.widgetId);
                } else {
                    for (const r of items.reservations) {
                        r.reservationSearchData = ReservationSearchData.deserialize(r.reservationSearchData);
                    }
                    output = items.reservations as ChosenReservationModel[];
                    if (savedBasket.shopStore) {
                        for (const r of savedBasket.shopStore.reservations) {
                            r.reservationSearchData = ReservationSearchData.deserialize(r.reservationSearchData);
                        }
                        NfHotelShopStore.shops = savedBasket.shopStore.shops;
                        NfHotelShopStore.reservations = savedBasket.shopStore.reservations;
                        NfHotelShopStore.widgetId = savedBasket.shopStore.widgetId;
                        if (NfHotelShopStore.languageId !== null && NfHotelShopStore.languageId !== savedBasket.shopStore.languageId) {
                            NfHotelShopStore.changeLanguage(NfHotelShopStore.languageId);
                        } else {
                            NfHotelShopStore.languageId = savedBasket.shopStore.languageId;
                        }
                    }
                    this.items = items;
                }
            }
        }
        return output;
    },

    removeLocalBasket() {
        if (this.widgetId) {
            this.items = null;
            localStorage.removeItem('nf-basket-' + this.widgetId);
            NfHotelShopStore.reset();
            if (+this.widgetId) {
                NfHotelShopStore.widgetId = +this.widgetId;
            }
        }
    },
});